import { useState } from 'react'

import { Controller, FormProvider } from 'react-hook-form'

import { PlanName, Role } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthToken, useNetworkRoles } from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'

import { useMemberCapacity } from '../../hooks/useMemberCapacity'
import { useSeatsCapacity } from '../../hooks/useSeatsCapacity'
import { getTranslatedPlanName } from '../../utils/plan'
import { CustomMessageInput } from './CustomMessageInput'
import { EmailNameArrayInput } from './EmailNameArrayInput'
import { RoleSelect } from './RoleSelect'
import { SpaceMultipleAutocomplete } from './SpaceMultipleAutocomplete'
import { useUserImportContext } from './UserImportContext'
import {
  ImportEntry,
  UserImportFormProps,
  useUserImportForm,
} from './useUserImportForm'
import { isAdminEmailNotConfirmed } from './utils'

export interface FormProps {
  onCancel: () => void
  onSubmit: (result: UserImportFormProps) => void
  loading: boolean
}

export const UserImportForm: React.FC<FormProps> = ({
  onSubmit,
  onCancel,
  loading,
}) => {
  const intl = useI18n()
  const { $t } = intl
  const context = useUserImportContext()
  const {
    data: { network, member },
  } = useAuthToken()
  const [customMessageExceedsLimit, setCustomMessageExceedsLimit] =
    useState(false)
  const roles = useNetworkRoles()
  const {
    didReachLimit,
    isApproachingLimit,
    isLoading: isMemberCapacityLoading,
    memberCapacity,
    totalInvitationCount,
  } = useMemberCapacity()
  const { seatsUsed, seatsCapacity } = useSeatsCapacity()

  const plan = network?.subscriptionPlan
  const trial = plan?.trial

  const shouldDisplayMembersCapacity =
    trial ||
    plan?.name === PlanName.Basic ||
    plan?.name === PlanName.basic ||
    isApproachingLimit ||
    didReachLimit

  const formMethods = useUserImportForm()

  const {
    form: methods,
    hideManyAtOnce,
    manyAtOnce,
    parseMultipleEmails,
  } = formMethods || {}

  // TODO: how to avoid any?
  const entries = ((methods as any).watch('entries') as ImportEntry[]) ?? []
  const role = (methods as any).watch('role') as Role
  const isStaff =
    role?.id === roles?.admin?.id || role?.id === roles?.moderator?.id

  const inviteesCount = entries.filter(
    entry => entry.name.length || entry.email.length,
  ).length

  const seatsLeft = seatsCapacity - seatsUsed
  const isExceedingStaffLimit =
    isStaff && inviteesCount > 0 && inviteesCount > seatsLeft

  const submitDisabled =
    isAdminEmailNotConfirmed(member) ||
    customMessageExceedsLimit ||
    isExceedingStaffLimit

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
  } = formMethods.form || {}

  const submit = data => {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { multipleEmails, ...rest } = data

    // After deleting last entry it's not being reflected in
    // the form's state so we get rid of empty internal entries
    const entries = rest.entries.filter(({ email }) => email)

    onSubmit({ ...rest, entries })
  }

  const invitationsLeft = memberCapacity - totalInvitationCount
  const planName = getTranslatedPlanName(intl, plan?.name, true)

  return (
    <FormProvider {...{ ...methods, ...formMethods }}>
      <form onSubmit={handleSubmit(submit)}>
        <div className="flex flex-col space-y-5">
          {!manyAtOnce ? (
            <>
              <EmailNameArrayInput name="entries" />

              {context && (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                <Controller
                  control={control}
                  name="spaces"
                  defaultValue={context?.defaultSpaces}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <FormControl>
                      <FormControl.Label>
                        <T
                          defaultMessage="{SPACES_CC} to join"
                          id="UserImport.Form.Spaces"
                        />
                      </FormControl.Label>
                      <SpaceMultipleAutocomplete
                        value={value}
                        onChange={onChange}
                      />
                      {error?.message && (
                        <FormControl.HelperText invalid className="mt-2">
                          {error?.message}
                        </FormControl.HelperText>
                      )}
                    </FormControl>
                  )}
                  rules={{
                    required: $t({
                      defaultMessage: 'This field can’t be empty',
                      id: 'Generics.Validation.RequiredField',
                    }),
                    validate: {
                      minlength: value =>
                        Array.isArray(value) && value.length > 0,
                    },
                  }}
                />
              )}

              {!!context && (
                <Controller
                  control={control}
                  name="role"
                  defaultValue={context?.defaultRole}
                  render={({
                    field: { onChange, onBlur, value, name },
                    fieldState: { error },
                  }) => (
                    <FormControl>
                      <FormControl.Label>
                        <T defaultMessage="Role" id="Generics.Role" />
                      </FormControl.Label>
                      <RoleSelect
                        name={name}
                        options={context.roles}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                      />
                      {error?.message && (
                        <FormControl.HelperText invalid className="mt-2">
                          {error?.message}
                        </FormControl.HelperText>
                      )}
                    </FormControl>
                  )}
                  rules={{
                    required: $t({
                      defaultMessage: 'This field can’t be empty',
                      id: 'Generics.Validation.RequiredField',
                    }),
                  }}
                />
              )}

              {isStaff && inviteesCount > 0 && (
                <div className="flex flex-col space-y-5">
                  {isExceedingStaffLimit ? (
                    <Alert status="error">
                      <T
                        defaultMessage="You are trying to invite {invitees_count, plural, =1 {# new staff} other {# new staffs}}, but {remaining_capacity, plural, =0 {there are no more seats available} =1 {there is only # seat available} other {there are only # seats available}}. Please contact the sales team to purchase more seats."
                        id="UserImport.ExceedingAdminSeats"
                        values={{
                          invitees_count: inviteesCount,
                          remaining_capacity: seatsLeft,
                          capacity: seatsCapacity,
                        }}
                      />
                    </Alert>
                  ) : (
                    <Alert status="neutral">
                      <T
                        defaultMessage="{invitees_count, plural, =1 {After sending the invitation} other {After sending these invitations}}, {remaining_seat_count, plural, =0 {no staff seats will remain} =1 {only # staff seat will remain} other {# staff seats will remain}}."
                        id="UserImport.RemainingAdminSeats"
                        values={{
                          invitees_count: inviteesCount,
                          remaining_seat_count: seatsLeft - inviteesCount,
                          capacity: seatsCapacity,
                        }}
                      />
                    </Alert>
                  )}
                </div>
              )}
              <CustomMessageInput
                setExceedsLimit={setCustomMessageExceedsLimit}
                register={register}
                errors={errors}
                setValue={setValue}
              />

              <div className="flex flex-col space-y-5">
                <div>
                  {!isStaff &&
                    !isMemberCapacityLoading &&
                    shouldDisplayMembersCapacity && (
                      <Alert
                        status={didReachLimit ? 'error' : 'warning'}
                        title=""
                      >
                        <T
                          defaultMessage="<strong>{remaining_invite_count, number} invitations</strong> left out of {capacity, number} for your {is_trial, select, true {trial} other {}} {plan_name} plan"
                          id="UserImport.RemainingInvites"
                          values={{
                            remaining_invite_count: invitationsLeft || 0,
                            capacity: memberCapacity || 0,
                            plan_name: planName,
                            is_trial: trial,
                          }}
                        />
                      </Alert>
                    )}
                </div>

                <Form.Actions>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={submitDisabled}
                    data-testid="submit-import-form"
                    loading={loading}
                  >
                    <T defaultMessage="Submit" id="Generics.Submit" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onCancel}
                    data-testid="cancel-import-form"
                  >
                    <T defaultMessage="Cancel" id="Generics.Cancel" />
                  </Button>
                </Form.Actions>
              </div>
            </>
          ) : (
            <div className="flex flex-col space-y-5">
              <FormControl.Textarea
                label={$t({
                  defaultMessage: 'Enter multiple email addresses',
                  id: 'UserImport.MultiEmailInput',
                })}
                helperText={$t({
                  defaultMessage: 'Use commas to separate addresses',
                  id: 'UserImport.MultiEmailInput.Helper',
                })}
                {...register('multipleEmails')}
                data-testid="invite-modal-multiple-emails-textarea"
                error={errors?.multipleEmails?.message}
              />
              <Form.Actions>
                <Button
                  variant="primary"
                  data-testid="invite-modal-add-invitees-button"
                  onClick={parseMultipleEmails}
                >
                  <T
                    defaultMessage="Add invitees"
                    id="UserImport.Actions.AddInvitees"
                  />
                </Button>
                <Button type="reset" variant="outline" onClick={hideManyAtOnce}>
                  <T defaultMessage="Back" id="Generics.Back" />
                </Button>
              </Form.Actions>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
